import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ChevronLeft, Eye, EyeOff, Plus, Search } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { Modal, PhoneInput, ACTION } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { AgentPageLayout } from '../components/layout/AgentPageLayout';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import { useAuth } from '../hooks/useAuth';
import ShopGovernanceModal, { type ShopConfirmModalState } from '../components/shops/ShopGovernanceModal';
import { ShopStatusBadge } from '../components/shops/shopStatusBadge';
import {
  createShop,
  fetchShops,
  suspendShop,
  unsuspendShop,
} from '../services/api';
import type { AgentShop } from '../types';
import { formatCostRate, percentInputToRate } from '../utils/costFormat';

interface CreateForm {
  name: string;
  username: string;
  email: string;
  phone: string;
  password: string;
  costPercent: string;
}

export default function ShopsPage() {
  const { user } = useAuth();
  const [shops, setShops] = useState<AgentShop[]>([]);
  const [agentCostRate, setAgentCostRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showShopPassword, setShowShopPassword] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<ShopConfirmModalState | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchShops();
      if (res.success && res.data) {
        setShops(res.data);
        if (res.agent_cost_rate != null && Number.isFinite(Number(res.agent_cost_rate))) {
          setAgentCostRate(Number(res.agent_cost_rate));
        }
      } else toast.error('Could not load Shops');
    } catch {
      toast.error('Could not load Shops');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const minShopCostExclusive = agentCostRate ?? user?.costRate ?? 0;
  const minShopCostPercent = Math.min(100, Math.max(0, minShopCostExclusive * 100));

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateForm>({
    defaultValues: {
      name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      costPercent: '0',
    },
  });

  const onCreate = async (data: CreateForm) => {
    const rate = percentInputToRate(data.costPercent);
    // Allow equality with agent cost rate; only reject when lower.
    if (rate + 1e-6 < minShopCostExclusive) {
      toast.error(
        `Shop cost must be at least your Agent cost (${(minShopCostExclusive * 100).toFixed(2)}%)`,
      );
      return;
    }
    try {
      const username = data.username.trim();
      const res = await createShop({
        name: data.name.trim(),
        username: username || undefined,
        email: data.email.trim(),
        phone: data.phone.trim() || undefined,
        password: data.password,
        cost_rate: percentInputToRate(data.costPercent),
      });
      if (!res.success) {
        toast.error(res.message || 'Could not create Shop');
        return;
      }
      toast.success('Shop created');
      closeCreateModal();
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not create Shop';
      toast.error(msg);
    }
  };

  const runRow = async (
    shopId: string,
    fn: () => Promise<{ success: boolean; message?: string }>,
    successMessage?: string,
  ): Promise<boolean> => {
    setRowBusy(shopId);
    try {
      const res = await fn();
      if (!res.success) {
        toast.error(res.message || 'Action failed');
        return false;
      }
      if (successMessage) toast.success(successMessage);
      await load();
      return true;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Action failed';
      toast.error(msg);
      return false;
    } finally {
      setRowBusy(null);
    }
  };

  const filteredShops = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return shops;
    return shops.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        (s.phone && s.phone.toLowerCase().includes(q)),
    );
  }, [shops, searchQuery]);

  const closeCreateModal = useCallback(() => {
    reset();
    setShowShopPassword(false);
    setShowCreate(false);
  }, [reset]);

  return (
    <AppLayout>
      <AgentPageLayout
        before={
          <Link
            to="/"
            className="inline-flex items-center gap-0.5 text-sm font-semibold text-orange-700 hover:text-orange-900"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {ACTION.backToDashboard}
          </Link>
        }
        title="Shops"
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-orange-700"
          >
            <Plus className="h-4 w-4" />
            Create Shop
          </button>
        }
      >
      {!loading && shops.length > 0 && (
        <div className="mb-4">
          <label htmlFor="shop-search" className="sr-only">
            Search Shops
          </label>
          <div className="relative w-full">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              id="shop-search"
              type="search"
              placeholder="Search by name, email, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
        </div>
      )}

      {loading ? (
        <PageLoadingIndicator variant="page" />
      ) : shops.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white">
          <PortalEmptyState
            icon="🏪"
            title="No Shops yet"
            description={
              <>
                Your list is empty until you create a Shop under this Agent. Create a Shop to issue logins, set cost
                rates and distribute credits to each Shop.
              </>
            }
            primaryAction={{
              kind: 'button',
              onClick: () => setShowCreate(true),
              label: '+ Create Shop',
            }}
          />
        </div>
      ) : filteredShops.length === 0 ? (
        <div className="rounded-xl border border-dashed border-amber-100 bg-amber-50/40 p-10 text-center">
          <p className="text-gray-700">No Shops match your search.</p>
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="mt-3 text-sm font-semibold text-orange-700 hover:text-orange-900"
          >
            Clear search
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {filteredShops.map((shop) => {
              const busy = rowBusy === shop.id;
              return (
                <article key={shop.id} className="rounded-xl border border-orange-100 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link to={`/shops/${shop.id}`} className="text-sm font-semibold text-orange-700 hover:underline">
                        {shop.name}
                      </Link>
                      <p className="mt-1 text-xs text-gray-600">{shop.email}</p>
                      <p className="text-xs text-gray-500">{shop.phone || '—'}</p>
                    </div>
                    <ShopStatusBadge status={shop.status} />
                  </div>
                  <p className="mt-2 text-xs text-gray-600">Cost: {formatCostRate(shop.costRate)}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to={`/shops/${shop.id}`}
                      className="rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-900 hover:bg-orange-100"
                    >
                      Sell credits
                    </Link>
                    {shop.status === 'active' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setConfirmModal({ type: 'suspend', shop })}
                        className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                      >
                        Suspend
                      </button>
                    )}
                    {shop.status === 'suspended' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => runRow(shop.id, () => unsuspendShop(shop.id), 'Shop reactivated')}
                        className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        {ACTION.reactivate}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
          <div className="hidden overflow-hidden rounded-xl border border-orange-100 bg-white shadow-sm md:block">
            <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-gray-100 bg-gray-50/80">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Cost</th>
                  <th className="px-4 py-3 font-semibold text-gray-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredShops.map((shop) => {
                  const busy = rowBusy === shop.id;
                  return (
                    <tr key={shop.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-medium">
                        <Link
                          to={`/shops/${shop.id}`}
                          className="text-orange-700 hover:text-orange-900 hover:underline"
                        >
                          {shop.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{shop.email}</td>
                      <td className="px-4 py-3 text-gray-600">{shop.phone || '—'}</td>
                      <td className="px-4 py-3">
                        <ShopStatusBadge status={shop.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatCostRate(shop.costRate)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Link
                            to={`/shops/${shop.id}`}
                            className="rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-900 hover:bg-orange-100"
                          >
                            Sell credits
                          </Link>
                          {shop.status === 'active' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setConfirmModal({ type: 'suspend', shop })}
                              className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                            >
                              Suspend
                            </button>
                          )}
                          {shop.status === 'suspended' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                runRow(shop.id, () => unsuspendShop(shop.id), 'Shop reactivated')
                              }
                              className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                            >
                              {ACTION.reactivate}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
      </AgentPageLayout>

      <ShopGovernanceModal
        state={confirmModal}
        onClose={() => setConfirmModal(null)}
        busyShopId={rowBusy}
        onConfirmSuspend={(id) => runRow(id, () => suspendShop(id), 'Shop suspended')}
      />

      <Modal
        isOpen={showCreate}
        onClose={closeCreateModal}
        variant="form"
        size="lg"
        title="Create Shop"
        subtitle="The Shop signs in with this email and password. Share credentials securely with the Shop owner."
      >
        <form onSubmit={handleSubmit(onCreate)} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Shop name</label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              autoComplete="off"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              {...register('username', {
                validate: (v) => {
                  const s = String(v ?? '').trim();
                  if (!s) return true; // optional
                  if (!/^[a-zA-Z0-9._-]{3,80}$/.test(s)) {
                    return '3–80 chars: letters, numbers, dot, underscore, dash';
                  }
                  return true;
                },
              })}
            />
            {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (login)</label>
            <input
              type="email"
              autoComplete="off"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              {...register('email', { required: 'Email is required' })}
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Phone number"
                  defaultCountry="US"
                />
              )}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <button
                type="button"
                className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowShopPassword((v) => !v)}
                aria-label={showShopPassword ? 'Hide password' : 'Show password'}
              >
                {showShopPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <input
                type={showShopPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="w-full rounded-lg border border-gray-200 py-2.5 pl-3 pr-10 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'At least 6 characters' },
                })}
              />
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost (%)</label>
            <input
              type="number"
              step="0.01"
              min={minShopCostPercent}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              {...register('costPercent', {
                validate: (v) => {
                  const r = percentInputToRate(String(v ?? ''));
                  // Allow equality with agent cost rate; only reject when lower.
                  if (r + 1e-6 < minShopCostExclusive) {
                    return `Must be at least ${(minShopCostExclusive * 100).toFixed(2)}% (your Agent cost)`;
                  }
                  return true;
                },
              })}
            />
            <p className="mt-1 text-xs text-gray-500">
              Must be at least {(minShopCostExclusive * 100).toFixed(2)}% (your Agent cost).
            </p>
            {errors.costPercent && (
              <p className="mt-1 text-sm text-red-600">{errors.costPercent.message}</p>
            )}
          </div>
          <div className="sm:col-span-2 flex gap-3 pt-1">
            <button
              type="submit"
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
            >
              Create Shop
            </button>
            <button
              type="button"
              onClick={closeCreateModal}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
